import React, { PropsWithChildren, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Flex } from "@chakra-ui/react";
import Header from "../../components/reusables/Header/Header";
import ServiceMenu from "../../components/ServiceMenu/ServiceMenu";
import Footer from "../../components/includes/Footer/Footer";
// import GetCategoriesFields from "../../components/GetCategoriesFields/GetCategoriesFields";
import { findMenuByPath } from "../../components/reusables/Navbar/navConfig";
import { useDispatch } from "react-redux";
// import { RootState } from "store/rootReducer";
import { bharatConnect } from "assets/images";
import CustomButton from "layouts/CustomButton/CustomButton";
// import { FormBuilder } from "layouts/UniversalField/UniversalField";
import { NewUniversalField } from "layouts/NewUniversalField/NewUniversalField";
import { useBillerList } from "hooks/Bbps/billerList.ts/useBillerList";
import { BillerMaster } from "features/bbps/biller/billerTypes";
import { mapCustomerParamsToFields } from "features/bbps/biller/billerFieldMapper";
import { useFormik } from "formik";
import { startProcess } from "store/features/billerDetails/billerDetailAction";

interface ServiceDetails {
  id?: string;
  name?: string;
  description?: string;
  icon?: React.ReactNode;
  fields?: Array<{
    name?: string;
    label?: string;
    type?: string;
    required?: boolean;
    placeholder?: string;
    prefix?: string;
    validation?: (value: string) => string | null;
  }>;
}

const ServicesPage: React.FC<PropsWithChildren<ServiceDetails>> = () => {
  const location = useLocation();
  const serviceItem = findMenuByPath(location.pathname);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const categoryApiName = serviceItem?.apiName; // ← API ke liye category key
  const categoryTitle = serviceItem?.label ?? "Service";

  const {
    billers,
    loading: billersLoading,
    error: billersError,
  } = useBillerList(categoryApiName);

  const [selectedBillerId, setSelectedBillerId] = useState<string | null>(null);

  const selectedBiller: BillerMaster | undefined = useMemo(
    () => billers.find((b) => b.billerId === selectedBillerId),
    [billers, selectedBillerId],
  );

  const { fields, initialValues, fieldIdToParamName } = useMemo(
    () => mapCustomerParamsToFields(selectedBiller?.billerCustomerParams),
    [selectedBiller],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values) => {
      if (!selectedBiller) return;

      // 1) form values -> customerParams map with ORIGINAL paramName (case-sensitive)
      const customerParams: Record<string, string> = {};
      Object.entries(values).forEach(([fieldId, v]) => {
        const originalName = fieldIdToParamName[fieldId];
        if (originalName) {
          customerParams[originalName] = String(v ?? "");
        }
      });

      // console.log({ values });

      // 2) capability flags
      // const { planMdmRequirement, fetchRequirement, supportBillValidation } =
      //   selectedBiller;

      // Yaha apna full flow likh sakte ho.
      // Pseudo:
      // if (isMandatory(planMdmRequirement)) { await fetchPlans(...) }
      // if (isMandatory(fetchRequirement)) { await billFetch(...) }
      // if (isMandatory(supportBillValidation)) { await billValidate(...) }
      // finally: await billPayment(...)

      // 3) API call

      // console.log("Submitting with:", {
      //   billerId: selectedBiller.billerId,
      //   customerParams,
      //   planMdmRequirement,
      //   fetchRequirement,
      //   supportBillValidation,
      // });

      const billerId = selectedBiller.billerId;
      const payload = {
        billerId: billerId,
        customerDetails: {
          "REMITTER NAME": "Customer",
        },
        customerMobileNumber: "7378926752",
        deviceDetails: {
          MAC: "54-E1-AD-27-FE-E3",
          IP: "192.168.1.6",
        },
        customerParams,
        timeStamp: "2025-11-13T01:35:56+05:30",
      };

      // console.log({payload});

      dispatch(startProcess({ billerId, values: payload }));
      navigate("/service-details");
      // if (fetchRequirement === "MANDATORY") {
      //   dispatch(startProcess({ billerId, values: payload }));
      //   navigate("/service-details");
      // }
    },
  });

  return (
    <Box bg={"brand.5"} position={"relative"} minH={"100vh"}>
      <Header />
      <ServiceMenu />

      <Box display={"flex"} flexWrap="wrap" bg="green.100">
        {/* LEFT 50% PANEL – keep existing UI layout */}
        <Box w={["100%", "50%", "35%"]}>
          <Box>
            <Box
              m={["1rem", "2rem", "3rem"]}
              rounded={["md", "lg", "xl"]}
              px={["15px", "15px", "35px"]}
              py={["5px", "10px", "15px"]}
              bg="white"
              boxShadow={"xl"}
            >
              <Box>
                <Flex justify="space-between">
                  <Box
                    fontSize={["sm", "md", "md"]}
                    fontWeight="bold"
                    p="0 0 0.5rem"
                  >
                    {categoryTitle}
                  </Box>
                  <Box w={[10, 12, 14]}>
                    <img src={bharatConnect} alt="logo" />
                  </Box>
                </Flex>

                {/* Biller dropdown + dynamic form */}
                <Box mt={3}>
                  {/* 1) Biller select (simple select; chahe to UniversalSelect use kar sakte ho) */}
                  <Box mb={3}>
                    <label className="block text-sm font-medium mb-1">
                      Select Biller
                    </label>
                    <select
                      className="w-full border rounded px-2 py-1 text-sm"
                      value={selectedBillerId ?? ""}
                      onChange={(e) =>
                        setSelectedBillerId(e.target.value || null)
                      }
                    >
                      <option value="">-- choose --</option>
                      {billers.map((b) => (
                        <option key={b.billerId} value={b.billerId}>
                          {b.billerName}
                        </option>
                      ))}
                    </select>

                    {billersLoading && (
                      <Box fontSize="xs" color="gray.500">
                        Loading billers…
                      </Box>
                    )}
                    {billersError && (
                      <Box fontSize="xs" color="red.500">
                        {billersError}
                      </Box>
                    )}
                  </Box>

                  {/* 2) Dynamic customer param form */}
                  {selectedBiller && fields?.length > 0 && (
                    <form onSubmit={formik.handleSubmit}>
                      <Box display="flex" flexDirection="column" gap={3}>
                        {fields.map((field) => (
                          <NewUniversalField
                            key={field.id}
                            field={field}
                            value={formik.values[field.id]}
                            onChange={(val) =>
                              formik.setFieldValue(field.id, val)
                            }
                            error={
                              (formik.touched as any)[field.id] &&
                              (formik.errors as any)[field.id]
                                ? String((formik.errors as any)[field.id])
                                : undefined
                            }
                          />
                        ))}

                        <CustomButton type="submit" onClick={() => {}}>
                          Submit
                        </CustomButton>
                      </Box>
                    </form>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* RIGHT side – abhi empty hai, baad me plans / bill summary / payment summary dikhane ke liye use kar sakte ho */}
        <Box flex="1" className="bg-green-50" />
      </Box>

      <Footer />
    </Box>
  );
};

export default ServicesPage;

// const ServicesPage: React.FC<PropsWithChildren<ServiceDetails>> = () => {
//   const { categoryName } = useParams<{ categoryName: string }>();

//   console.log({ categoryName });

//   return (
//     <Box bg={"brand.5"} position={"relative"} minH={"100vh"}>
//       <Header />
//       <ServiceMenu />
//       <Box display={"flex"} flexWrap="wrap" bg="green.100">
//         <Box w={["100%", "50%", "35%"]}>
//           <Box>
//             <Box
//               m={["1rem", "2rem", "3rem"]}
//               rounded={["md", "lg", "xl"]}
//               px={["15px", "15px", "35px"]}
//               py={["5px", "10px", "15px"]}
//               bg="white"
//               boxShadow={"xl"}
//             >
//               <Box>
//                 <Flex justify="space-between">
//                   <Box
//                     fontSize={["sm", "md", "md"]}
//                     fontWeight="bold"
//                     p="0 0 0.5rem"
//                   >
//                     {"category title"}
//                   </Box>
//                   <Box w={16}>
//                     <Image src={bharatConnect} alt="logo" />
//                   </Box>
//                 </Flex>
//                 <Box>
//                   <Box>
//                     <Box>
//                       {"biller data from"}
//                     </Box>
//                     <Box>
//                       <CustomButton onClick={() => {console.log("clicked")}}>Submit</CustomButton>
//                     </Box>
//                   </Box>
//                 </Box>
//               </Box>
//             </Box>{" "}
//           </Box>
//         </Box>
//         <Box></Box>
//       </Box>
//       <Footer />
//     </Box>
//   );
// };
// export default ServicesPage;

// import React, { PropsWithChildren } from "react";
// import { useParams } from "react-router-dom";
// import { Box } from "@chakra-ui/react";
// import Header from "../../components/reusables/Header/Header";
// import ServiceMenu from "../../components/ServiceMenu/ServiceMenu";
// import Footer from "../../components/includes/Footer/Footer";
// import GetCategoriesFields from "../../components/GetCategoriesFields/GetCategoriesFields";
// import { findMenuByPath } from "../../components/reusables/Navbar/navConfig";
// import { useSelector } from "react-redux";
// import { RootState } from "store/rootReducer";

// interface ServiceDetails {
//   id?: string;
//   name?: string;
//   description?: string;
//   icon?: React.ReactNode;
//   fields?: Array<{
//     name?: string;
//     label?: string;
//     type?: string;
//     required?: boolean;
//     placeholder?: string;
//     prefix?: string;
//     validation?: (value: string) => string | null;
//   }>;
// }

// const ServicesPage: React.FC<PropsWithChildren<ServiceDetails>> = () => {
//   const { serviceId } = useParams<{ serviceId: string }>();
//   // const navigate = useNavigate();

//   const serviceObject = findMenuByPath(serviceId!);

//   return (
//     <Box bg={"brand.5"} position={"relative"} minH={"100vh"}>
//       <Header />
//       <ServiceMenu />
//       <Box display={"flex"} flexWrap="wrap" bg="green.100">
//         <Box w={["100%", "50%", "35%"]}>
//           <Box>
//             <GetCategoriesFields serviceObject={serviceObject} />
//           </Box>
//         </Box>
//         <Box>

//         </Box>
//       </Box>
//       <Footer />
//     </Box>
//   );
// };

// export default ServicesPage;
